import { Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { BryntumGridModule, BryntumDateRangeFieldComponent, BryntumComboComponent } from '@bryntum/grid-angular';
import { NgbDatepickerModule, NgbNavModule, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { NgIcon } from '@ng-icons/core';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { containerSearchInitiated } from '../../../../../shared/Container/container.actions';
import { IRootState } from '../../../../app.config';
import { selectContainerSearch } from '../../../../../shared/Container/container.reducer';
import { Subject, debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs';
import { SearchDateOption } from '../../../../../shared/Container/enum/SearchDateOption';

@Component({
  selector: 'app-container-job-search-form',
  standalone: true,
  imports: [NgbDatepickerModule, BryntumGridModule, NgIcon, NgbNavModule],
  templateUrl: './container-job-search-form.component.html',
  styleUrl: './container-job-search-form.component.scss'
})
export class ContainerJobSearchFormComponent implements OnDestroy {
  calendar = inject(NgbCalendar);
  readonly defaultSearchDateOption = SearchDateOption.SearchByVesselETAD;

  @ViewChild('dateFilterInput')
  dateFilterInput: BryntumDateRangeFieldComponent | null = null;

  @ViewChild('searchDateOptionInput')
  searchDateOptionInput: BryntumComboComponent | null = null;

  dateRangeFilterComboConfig: Partial<BryntumComboComponent> | any = {
    items: [
      { text: 'Vessel ETA/D', value: SearchDateOption.SearchByVesselETAD },
      { text: 'Job Date', value: SearchDateOption.SearchByJobDate },
      { text: 'Requested Delivery Date', value: SearchDateOption.SearchByRequestedDeliveryDate },
      { text: 'Execution Date', value: SearchDateOption.SearchByExecutionDate },
    ],
    value: this.defaultSearchDateOption,
    width: '10em',
    listeners: {
      change: (event: any) => {
        const searchDateOption = event.value as SearchDateOption;
        this.dateRangeFilterComboConfig.value = searchDateOption;
        this.queueDateRangeChange(this.dateRangeConfig.value, searchDateOption);
      },
    }
  }

  defaultDaysToFilter: number = 7;
  private readonly dateRangeChanges$ = new Subject<{ from: Date, to: Date, searchDateOption: SearchDateOption }>();
  private readonly destroy$ = new Subject<void>();

  dateRangeConfig: Partial<BryntumDateRangeFieldComponent> | any = {
    format: 'DD/MM/YYYY',
    value: [dayjs().subtract(this.defaultDaysToFilter, 'days'), dayjs()],
    listeners: {
      change: (event: any) => {
        this.dateRangeConfig.value = event.value;
        this.queueDateRangeChange(event.value);
      },
      catchAll: (event: any) => {
        if (event.type === 'paint' && event.firstPaint) {
          this.store$.select(selectContainerSearch).pipe(take(1)).subscribe(result => {
            if (result) {
              this.dateRangeConfig.value = [dayjs(result.from), dayjs(result.to)];
              this.setSearchDateOption(result.searchDateOption);
            } else {
              this.resetFilters();
            }
          });
        }
      }
    }
  }

  constructor(private store$: Store<IRootState>) {
    this.store$.select(selectContainerSearch).pipe(take(1)).subscribe(result => {
      if (result) {
        this.dateRangeConfig.value = [dayjs(result.from), dayjs(result.to)];
        this.setSearchDateOption(result.searchDateOption);
      }
    });

    this.dateRangeChanges$.pipe(
      debounceTime(300),
      distinctUntilChanged((previous, current) =>
        dayjs(previous.from).isSame(current.from, 'day')
        && dayjs(previous.to).isSame(current.to, 'day')
        && previous.searchDateOption === current.searchDateOption
      ),
      takeUntil(this.destroy$)
    ).subscribe(searchParams => {
      this.store$.dispatch(containerSearchInitiated({ searchParams }));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private queueDateRangeChange(
    value: unknown,
    searchDateOption = this.dateRangeFilterComboConfig.value as SearchDateOption
  ): void {
    if (!Array.isArray(value) || value.length < 2 || !value[0] || !value[1]) {
      return;
    }

    const from = dayjs(value[0]);
    const to = dayjs(value[1]);
    if (!from.isValid() || !to.isValid()) {
      return;
    }

    this.dateRangeChanges$.next({
      from: from.toDate(),
      to: to.toDate(),
      searchDateOption
    });
  }

  private setSearchDateOption(searchDateOption: SearchDateOption): void {
    this.dateRangeFilterComboConfig.value = searchDateOption;
    if (this.searchDateOptionInput) {
      this.searchDateOptionInput.instance.value = searchDateOption;
    }
  }

  resetFilters() {
    this.dateRangeConfig.value = [dayjs().subtract(this.defaultDaysToFilter, 'days'), dayjs()];
    this.setSearchDateOption(this.defaultSearchDateOption);
    this.queueDateRangeChange(this.dateRangeConfig.value, this.defaultSearchDateOption);
  }

  getNormalizeDateRange() {
    if (this.dateFilterInput === null) {
      return {
        from: new Date(dayjs().add(1, 'day').subtract(this.defaultDaysToFilter, 'days').format()),
        to: new Date(dayjs().add(1, 'day').format())
      };
    } else {

      const fromDayJs = dayjs(this.dateFilterInput.instance.value[0]).add(1, 'day');
      const toDayJs = dayjs(this.dateFilterInput.instance.value[1]).add(1, 'day');

      return {
        from: new Date(fromDayJs.toString()),
        to: new Date(toDayJs.toString())
      };
    }
  }
}
